import { Badge } from '@monksflow/monks-ui';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';

export interface InterviewPersonaCardProps {
  name: string;
  age?: string;
  title: string;
  tags?: string[];
  description?: string;
  photoAssetId?: string;
  onClick?: () => void;
}

export function InterviewPersonaCard({
  name,
  age,
  title,
  tags = [],
  description,
  photoAssetId,
  onClick,
}: InterviewPersonaCardProps) {
  const { downloadUrl: photoUrl } = useGetDownloadUrl(photoAssetId);
  const displayName = age ? `${name}, ${age}` : name;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center overflow-hidden rounded-3xl border border-border bg-card text-left transition-shadow duration-fast ease-out hover:shadow-md outline-none focus-visible:focus-ring"
    >
      {/* Image section. The backdrop keeps the white name readable before or without a photo. */}
      <div className="relative flex-1 self-stretch overflow-hidden rounded-bl-3xl rounded-tl-3xl bg-backdrop">
        {photoUrl && (
          <img
            src={photoUrl}
            alt={displayName}
            className="absolute inset-0 size-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent from-80% to-black/50" />
        <div className="absolute bottom-2 left-6 text-base font-medium leading-6 tracking-tight text-white">
          {displayName}
        </div>
      </div>

      {/* Content section */}
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-xl leading-7 tracking-tight text-foreground">{title}</h3>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="outline" size="sm" title={tag}>
                  <span className="truncate">{tag}</span>
                </Badge>
              ))}
            </div>
          )}
        </div>
        {description && (
          <p className="line-clamp-2 text-sm leading-5 tracking-tight text-muted-foreground">
            {description}
          </p>
        )}
      </div>
    </button>
  );
}
